import { Patient } from "../../../lib/types/patient";
import { Card, CardContent } from "../../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { format } from "date-fns";
import { PatientVisitHistory } from "../../visits/components/PatientVisitHistory";
import { PatientFollowUps } from "../../visits/components/PatientFollowUps";

interface PatientDetailsProps {
  patient: Patient;
}

export function PatientDetails({ patient }: PatientDetailsProps) {
  // Since we're viewing an existing patient, we know it has an ID
  const patientId = patient.id as string;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">
          {patient.firstName} {patient.lastName}
        </h1>
        <p className="text-sm text-muted-foreground">
          Patient ID: {patientId}
        </p>
      </div>

      <Card>
        <CardContent className="p-6">
          <Tabs defaultValue="demographics">
            <TabsList className="mb-6">
              <TabsTrigger value="demographics">Demographics</TabsTrigger>
              <TabsTrigger value="medical">Medical History</TabsTrigger>
              <TabsTrigger value="insurance">Insurance</TabsTrigger>
              <TabsTrigger value="visits">Visit History</TabsTrigger>
              <TabsTrigger value="followups">Follow-ups</TabsTrigger>
            </TabsList>

            <div className="h-[400px] overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/20 hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/30">
              <TabsContent value="demographics" className="p-4">
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Date of Birth</dt>
                    <dd className="text-sm text-foreground mt-1">
                      {format(new Date(patient.dateOfBirth), "MMMM d, yyyy")}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Gender</dt>
                    <dd className="text-sm text-foreground capitalize mt-1">{patient.gender}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Phone</dt>
                    <dd className="text-sm text-foreground mt-1">{patient.phone}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Email</dt>
                    <dd className="text-sm text-foreground mt-1">{patient.email}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-sm font-medium text-muted-foreground">Address</dt>
                    <dd className="text-sm text-foreground mt-1">
                      {patient.address.street}<br />
                      {patient.address.city}, {patient.address.state} {patient.address.zipCode}
                    </dd>
                  </div>
                </dl>
              </TabsContent>

              <TabsContent value="medical" className="p-4">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-2">Medical History</h3>
                    <p className="text-sm text-foreground whitespace-pre-wrap">
                      {patient.medicalHistory || <span className="text-muted-foreground italic">No medical history recorded</span>}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-2">Allergies</h3>
                    <p className="text-sm text-foreground whitespace-pre-wrap">
                      {patient.allergies || <span className="text-muted-foreground italic">No allergies recorded</span>}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-2">Current Medications</h3>
                    <ul className="list-disc list-inside space-y-1">
                      {patient.medications.length > 0 ? (
                        patient.medications.map((med, i) => (
                          <li key={i} className="text-sm text-foreground">
                            {med.name} - {med.dosage} ({med.frequency})
                          </li>
                        ))
                      ) : (
                        <li className="text-sm text-muted-foreground italic">No current medications</li>
                      )}
                    </ul>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="insurance" className="p-4">
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Provider</dt>
                    <dd className="text-sm text-foreground mt-1">
                      {patient.insuranceInfo.provider || 'Not provided'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Policy Number</dt>
                    <dd className="text-sm text-foreground mt-1">
                      {patient.insuranceInfo.policyNumber || 'Not provided'}
                    </dd>
                  </div>
                  {patient.insuranceInfo.groupNumber && (
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">Group Number</dt>
                      <dd className="text-sm text-foreground mt-1">
                        {patient.insuranceInfo.groupNumber}
                      </dd>
                    </div>
                  )}
                </dl>
              </TabsContent>

              <TabsContent value="visits" className="p-4">
                <PatientVisitHistory patientId={patientId} />
              </TabsContent>

              <TabsContent value="followups" className="p-4">
                <PatientFollowUps patientId={patientId} />
              </TabsContent>
            </div>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
